import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { FiX, FiPlus } from 'react-icons/fi';
import EditableCell from '../EditableCell';
import { formatDateDMY } from '../../../../utils/formatDate';
import Spinner from '../Spinner';
import PaymentList from '../PaymentList';
import {
  fetchPaymentList,
  fetchDebts,
  debtOverview,
  addPayment,
  editPayment,
  deletePayment,
  selectPaymentList,
  selectDebtLoading
} from '../../../../app/store/slices/debtSlice';
import {
  fetchMortgagePayments,
  fetchMortgages,
  mortgageOverview,
  addMortgagePayment,
  editMortgagePayment,
  deleteMortgagePayment,
  selectMortgagePayments,
  selectMortgagePaymentsLoading
} from '../../../../app/store/slices/mortgageSlice';
import {
  fetchSavings, savingOverview,
  fetchContributions,
  addContribution,
  editContribution,
  deleteContribution,
  selectContributions,
  selectContributionsLoading
} from '../../../../app/store/slices/savingSlice';
import {formatAmount} from '../../../../utils/formatAmount';

const GenericDetailsModal = ({
  show,
  onClose,
  item,
  onUpdateItem,
  onAddTransaction,
  baseCurrency,
  accounts,
  isUpdating = false,
  modalType = 'saving'
}) => {
  const [editingCells, setEditingCells] = useState({});
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [transactionAmount, setTransactionAmount] = useState('');
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const dispatch = useDispatch();
  const payments = useSelector(selectPaymentList);
  const paymentsLoading = useSelector(selectDebtLoading);
  const mortgagePayments = useSelector(selectMortgagePayments);
  const mortgagePaymentsLoading = useSelector(selectMortgagePaymentsLoading);
  const contributions = useSelector(selectContributions);
  const contributionsLoading = useSelector(selectContributionsLoading);


  useEffect(() => {
    if ((modalType === 'debt' || modalType === 'mortgage') && item?._id && item.canvasId) {
      dispatch(fetchPaymentList({ canvasId: item.canvasId, debtId: item._id }));
    }
  }, [dispatch, item?._id, item?.canvasId, modalType]);

  useEffect(() => {
    if (modalType === 'mortgage' && item?._id && item.canvasId) {
      dispatch(fetchMortgagePayments({ canvasId: item.canvasId, mortgageId: item._id }));
    }
  }, [dispatch, item?._id, item?.canvasId, modalType]);

  useEffect(() => {
    if (modalType === 'saving' && item?._id && item.canvasId) {
      dispatch(fetchContributions({ canvasId: item.canvasId, savingId: item._id }));
    }
  }, [dispatch, item?._id, item?.canvasId, modalType]);

  if (!show || !item) return null;

  const formatCurrency = (amount) => {
    return `${baseCurrency} ${formatAmount(amount)}`;
  };

  const getModalConfig = () => {
    switch (modalType) {
      case 'saving':
        return {
          title: 'Saving Goal Details',
          transactionLabel: 'Add Contribution',
          transactionButton: 'Add Contribution',
          editableFields: [
            { key: 'targetAmount', label: 'Target Amount', type: 'currency' },
            { key: 'targetContributionPerMonth', label: 'Target Contribution/Month', type: 'currency' },
          ],
          autoCalculatedFields: [
            { label: 'Amount Saved', value: formatCurrency(item.currentAmount) },
            { label: 'Left to Save', value: formatCurrency(Math.max(item.targetAmount - item.currentAmount, 0)) },
            { label: 'Saving Rate', value: `${item.targetAmount > 0 ? ((item.currentAmount / item.targetAmount) * 100).toFixed(1) : 0}%` },
            { label: 'Est. Target Reach Date', value: formatDateDMY(item?.targetDate) },
          ]
        };
      case 'debt':
        return {
          title: 'Debt Details',
          transactionLabel: 'Record Payment',
          transactionButton: 'Add Payment',
          editableFields: [
            { key: 'initialAmount', label: 'Initial Amount', type: 'currency' },
            { key: 'annualInterestRate', label: 'Interest Rate (%)', type: 'percentage' },
            { key: 'monthlyPayment', label: 'Monthly Payment', type: 'currency' },
            { key: 'dueDate', label: 'Payment Due Date', type: 'date' },
          ],
          autoCalculatedFields: [
            { label: 'Amount Repaid', value: formatCurrency(item.amountRepaid) },
            { label: 'Balance', value: formatCurrency(item.balance || (item.initialAmount - (item.amountRepaid || 0))), color: 'text-red-700' },
            { label: 'Est. Final Payment Date', value: formatDateDMY(item.estimatedFinalPaymentDate) },
          ]
        };
      case 'mortgage':
        return {
          title: 'Mortgage Details',
          transactionLabel: 'Record Payment',
          transactionButton: 'Add Payment',
          editableFields: [
            { key: 'initialAmount', label: 'Initial Amount', type: 'currency' },
            { key: 'annualInterestRate', label: 'Interest Rate (%)', type: 'percentage' },
            { key: 'monthlyPayment', label: 'Monthly Payment', type: 'currency' },
            { key: 'monthlyPaymentDueDate', label: 'Payment Due Date', type: 'date' },
            { key: 'propertyValue', label: 'Property Value', type: 'currency' },
          ],
          autoCalculatedFields: [
            { label: 'Amount Repaid', value: formatCurrency(item.amountRepaid) },
            { label: 'Balance', value: formatCurrency(item.balance || (item.initialAmount - (item.amountRepaid || 0))), color: 'text-red-700' },
            { label: 'Equity', value: formatCurrency((item.propertyValue || 0) - (item.balance || 0)) },
            { label: 'Est. Final Payment Date', value: formatDateDMY(item.estimatedFinalPaymentDate) },
          ]
        };
      default:
        return {};
    }
  };

  const config = getModalConfig();

  const handleCellEdit = (field) => {
    setEditingCells(prev => ({ ...prev, [field]: true }));
  };

  const handleCellSave = async (field, value) => {
    try {
      let processedValue = value;
      const fieldConfig = config.editableFields.find(f => f.key === field);

      if (fieldConfig?.type === 'currency' || fieldConfig?.type === 'percentage') {
        processedValue = Number(value);
      }

      await onUpdateItem(item._id, { [field]: processedValue });
      setEditingCells(prev => ({ ...prev, [field]: false }));
    } catch (err) {
      setEditingCells(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleCellCancel = (field) => {
    setEditingCells(prev => ({ ...prev, [field]: false }));
  };

  // Add Payment
  const handleAddTransaction = async (e) => {
    e.preventDefault();
    if (!transactionAmount || Number(transactionAmount) <= 0) return;
    setIsAddingPayment(true);
    try {
      if (modalType === 'saving') {
        await dispatch(addContribution({
          canvasId: item.canvasId,
          savingId: item._id,
          amount: Number(transactionAmount),
          date: new Date().toISOString().split('T')[0]
        })).unwrap();
        // Refresh savings and overview
        dispatch(fetchSavings({ canvasId: item.canvasId }));
        dispatch(savingOverview({ canvasId: item.canvasId }));
        dispatch(fetchContributions({ canvasId: item.canvasId, savingId: item._id }));
      } else if (modalType === 'debt') {
        await dispatch(addPayment({
          canvasId: item.canvasId,
          debtId: item._id,
          amount: Number(transactionAmount),
          date: new Date().toISOString().split('T')[0]
        })).unwrap();
        dispatch(fetchDebts({ canvasId: item.canvasId }));
        dispatch(debtOverview({ canvasId: item.canvasId }));
        dispatch(fetchPaymentList({ canvasId: item.canvasId, debtId: item._id }));
      } else if (modalType === 'mortgage') {
        await dispatch(addMortgagePayment({
          canvasId: item.canvasId,
          mortgageId: item._id,
          amount: Number(transactionAmount),
          date: new Date().toISOString().split('T')[0]
        })).unwrap();
        dispatch(fetchMortgages({ canvasId: item.canvasId }));
        dispatch(mortgageOverview({ canvasId: item.canvasId }));
        dispatch(fetchMortgagePayments({ canvasId: item.canvasId, mortgageId: item._id }));
      }
      setTransactionAmount('');
      setShowAddTransaction(false);
    } catch (err) {}
    setIsAddingPayment(false);
  };

  // Edit Payment
  const handleEditPayment = async (paymentId, amount, date) => {
    setEditingId(paymentId);
    try {
      await dispatch(editPayment({
        canvasId: item.canvasId,
        paymentId,
        amount: Number(amount),
        date
      })).unwrap();
      if (modalType === 'debt') {
        dispatch(fetchDebts({ canvasId: item.canvasId }));
        dispatch(debtOverview({ canvasId: item.canvasId }));
        dispatch(fetchPaymentList({ canvasId: item.canvasId, debtId: item._id }));
      }
    } catch (err) {}
    setEditingId(null);
  };

  // Delete Payment
  const handleDeletePayment = async (paymentId) => {
    setDeletingId(paymentId);
    try {
      await dispatch(deletePayment({
        canvasId: item.canvasId,
        paymentId
      })).unwrap();
      if (modalType === 'debt') {
        dispatch(fetchDebts({ canvasId: item.canvasId }));
        dispatch(debtOverview({ canvasId: item.canvasId }));
        dispatch(fetchPaymentList({ canvasId: item.canvasId, debtId: item._id }));
      }
    } catch (err) {}
    setDeletingId(null);
  };

  // // Add Mortgage Payment
  // const handleAddMortgagePayment = async (e) => {
  //   e.preventDefault();
  //   if (!transactionAmount || Number(transactionAmount) <= 0) return;
  //   try {
  //     await dispatch(addMortgagePayment({
  //       canvasId: item.canvasId,
  //       mortgageId: item._id,
  //       amount: Number(transactionAmount),
  //       date: new Date().toISOString().split('T')[0]
  //     })).unwrap();
  //     setTransactionAmount('');
  //     setShowAddTransaction(false);
  //   } catch (err) {}
  // };

  // Edit Mortgage Payment
  const handleEditMortgagePayment = async (paymentId, amount, date) => {
    setEditingId(paymentId);
    try {
      await dispatch(editMortgagePayment({
        canvasId: item.canvasId,
        paymentId,
        amount: Number(amount),
        date
      })).unwrap();
      // Refresh mortgages
      if (modalType === 'mortgage') {
        dispatch(fetchMortgages({ canvasId: item.canvasId }));
        dispatch(mortgageOverview({ canvasId: item.canvasId }));
        dispatch(fetchMortgagePayments({ canvasId: item.canvasId, mortgageId: item._id }));
      }
    } catch (err) {}
    setEditingId(null);
  };

  // Delete Mortgage Payment
  const handleDeleteMortgagePayment = async (paymentId) => {
    setDeletingId(paymentId);
    try {
      await dispatch(deleteMortgagePayment({
        canvasId: item.canvasId,
        paymentId
      })).unwrap();
      // Refresh mortgages
      if (modalType === 'mortgage') {
        dispatch(fetchMortgages({ canvasId: item.canvasId }));
        dispatch(mortgageOverview({ canvasId: item.canvasId }));
        dispatch(fetchMortgagePayments({ canvasId: item.canvasId, mortgageId: item._id }));
      }
    } catch (err) {}
    setDeletingId(null);
  };

  const handleEditContribution = async (contributionId, amount, date) => {
    setEditingId(contributionId);
    try {
      await dispatch(editContribution({
        canvasId: item.canvasId,
        contributionId,
        amount: Number(amount),
        date
      })).unwrap();
      dispatch(fetchSavings({ canvasId: item.canvasId }));
      dispatch(savingOverview({ canvasId: item.canvasId }));
      dispatch(fetchContributions({ canvasId: item.canvasId, savingId: item._id }));
    } catch (err) {}
    setEditingId(null);
  };

  const handleDeleteContribution = async (contributionId) => {
    setDeletingId(contributionId);
    try {
      await dispatch(deleteContribution({
        canvasId: item.canvasId,
        contributionId
      })).unwrap();
      dispatch(fetchSavings({ canvasId: item.canvasId }));
      dispatch(savingOverview({ canvasId: item.canvasId }));
      dispatch(fetchContributions({ canvasId: item.canvasId, savingId: item._id }));
    } catch (err) {}
    setDeletingId(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{item.title || item.name}</h2>
            <p className="text-sm text-gray-600">{config.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-btn-primary/10 rounded-lg transition-colors"
            title="Close"
          >
            <FiX className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
              <h3 className="font-semibold text-btn-primary mb-3">Editable Fields</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Start Date</span>
                  <span className="font-medium">{formatDateDMY(item.createdAt || item.startDate)}</span>
                </div>
                {config.editableFields?.map((field) => (
                  <div key={field.key} className="flex justify-between items-center">
                    <span className="text-gray-600">{field.label}</span>
                    <EditableCell
                      value={item[field.key] || 0}
                      isEditing={editingCells[field.key]}
                      isUpdating={isUpdating}
                      onEdit={() => handleCellEdit(field.key)}
                      onSave={(value) => handleCellSave(field.key, value)}
                      onCancel={() => handleCellCancel(field.key)}
                      inputType={field.type === 'date' ? 'date' : 'number'}
                      inputProps={field.type !== 'date' ? { min: 0, step: field.type === 'percentage' ? 0.01 : 0.01 } : {}}
                      displayFormat={field.type === 'currency' ? formatCurrency : field.type === 'percentage' ? (val) => `${val}%` : field.type === 'date' ? formatDateDMY : undefined}
                      editTitle={`Click to edit ${field.label.toLowerCase()}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Auto-calculated Fields */}
            <div className="bg-btn-secondary/10 p-4 rounded-xl border border-btn-secondary/20">
              <h3 className="font-semibold text-btn-primary mb-3">Auto-calculated</h3>
              <div className="space-y-3">
                {config.autoCalculatedFields?.map((field, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-gray-600">{field.label}</span>
                    <span className={`font-medium ${field.color || ''}`}>{field.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">
                {modalType === 'saving' ? 'Progress' : 'Repaid'}
              </span>
              <span className="font-semibold text-btn-primary">
                {modalType === 'saving'
                  ? `${item.targetAmount > 0 ? ((item.currentAmount / item.targetAmount) * 100).toFixed(1) : 0}%`
                  : `${item.initialAmount > 0 ? (((item.amountRepaid || 0) / item.initialAmount) * 100).toFixed(1) : 0}%`
                }
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className='h-3 rounded-full transition-all duration-300 bg-btn-primary'
                style={{
                  width: `${Math.min(
                    modalType === 'saving'
                      ? (item.targetAmount > 0 ? (item.currentAmount / item.targetAmount) * 100 : 0)
                      : (item.initialAmount > 0 ? ((item.amountRepaid || 0) / item.initialAmount) * 100 : 0),
                    100
                  )}%`
                }}
              ></div>
            </div>
          </div>

          {/* Add Transaction Section */}
          <div className="bg-btn-primary/10 p-4 rounded-xl border border-btn-primary/20">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-btn-primary">{config.transactionLabel}</h3>
              {!showAddTransaction && (
                <button
                  onClick={() => setShowAddTransaction(true)}
                  className="flex items-center gap-2 bg-btn-primary text-white px-3 py-1.5 rounded-lg text-sm hover:bg-btn-primary-hover transition-colors"
                  type="button"
                >
                  <FiPlus className="w-4 h-4" />
                  {isAddingPayment ? <Spinner/> : "Add"}
                </button>
              )}
            </div>

            {showAddTransaction && (
              <form onSubmit={handleAddTransaction} className="flex gap-2 mt-3">
                <input
                  type="number"
                  value={transactionAmount}
                  onChange={(e) => setTransactionAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent text-sm"
                  required
                  disabled={isUpdating}

                />
                 <button
                  type="submit"
                  disabled={isAddingPayment}
                  className="bg-btn-primary hover:bg-btn-primary-hover text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
                >
                  {isAddingPayment ? <Spinner text="" size="small" /> : "Add"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTransaction(false);
                    setTransactionAmount('');
                  }}
                  className="bg-white text-black px-3 py-1.5 rounded-lg text-sm"
                  disabled={isUpdating}
                >
                  Cancel
                </button>
              </form>
            )}
          </div>

          {/* Additional Info */}
          {item.notes && (
            <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
              <p className="text-gray-600">{item.notes}</p>
            </div>
          )}

          {/* Payment History */}
          {(modalType === 'debt' || modalType === 'mortgage') && (
            <div className="mt-6">
              <h3 className="font-semibold text-btn-primary mb-2">Payment History</h3>
              <PaymentList
                payments={modalType === 'debt' ? payments : mortgagePayments}
                onEdit={modalType === 'debt' ? handleEditPayment : handleEditMortgagePayment}
                onDelete={modalType === 'debt' ? handleDeletePayment : handleDeleteMortgagePayment}
                isLoading={modalType === 'debt' ? paymentsLoading : mortgagePaymentsLoading}
              />
            </div>
          )}

          {/* Contributions History */}
          {modalType === 'saving' && (
            <div className="mt-6">
              <h3 className="font-semibold text-btn-primary mb-2">Contributions</h3>
              <PaymentList
                payments={contributions}
                onEdit={handleEditContribution}
                onDelete={handleDeleteContribution}
                isLoading={contributionsLoading}
                editingId={editingId}
                deletingId={deletingId}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenericDetailsModal;